import React, { useEffect, ReactNode, useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import * as lib from '~/dependencies';
import settings from '~/assets/settings.json';
import {
  ThumbsContainer,
  Thumb,
  ThumbInner,
  ThumbImage,
  NonImagePreview,
  NonImageBadge,
  NonImageName,
  RemoveImageButton,
} from './styles';

interface FilePreview extends File {
  preview?: string;
}

interface PreviewsProps {
  children?: ReactNode;
  fileInputRef?: React.RefObject<HTMLInputElement>;
  onFileChange: (file: FilePreview | undefined) => void;
  onPreviewChange: (file: File[]) => void;
  files: File[];
}

function isImageFile(file: FilePreview): boolean {
  return file.type.startsWith('image/');
}

function getFileExtension(fileName: string): string {
  const segments = fileName.split('.');
  if (segments.length < 2) {
    return 'FILE';
  }

  return segments[segments.length - 1].toUpperCase();
}

const BLOCKED_EXTENSIONS = new Set(['aac', 'mov', 'ogg', 'm4a']);

function FilePickerPreview(props: PreviewsProps) {
  const {
    children,
    files,
    fileInputRef,
    onPreviewChange,
    onFileChange,
  } = props;

  const resetNativeInputValue = useCallback(() => {
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  }, [fileInputRef]);

  const onDropRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      const firstRejection = fileRejections[0];
      const rejectionCode = firstRejection?.errors[0]?.code;
      const fileName = firstRejection?.file?.name;

      const rejectionMessages: Record<
        string,
        { message: string; type: 'info' | 'warn' | 'error' }
      > = {
        'file-too-large': {
          message:
            'File size exceeds the maximum allowed limit for attachments.',
          type: 'error',
        },
        'file-invalid-type': {
          message: 'File type is invalid for attachments.',
          type: 'warn',
        },
        'file-blocked-extension': {
          message: 'This file format is not supported for attachments.',
          type: 'warn',
        },
        'too-many-files': {
          message: 'Only one file can be uploaded at a time.',
          type: 'info',
        },
      };

      const rejection = rejectionCode
        ? rejectionMessages[rejectionCode]
        : undefined;

      lib.ToastMessage(
        rejection?.type || 'error',
        rejection?.message ||
          `The file ${fileName || 'selected'} is not a supported format`
      );
      resetNativeInputValue();
    },
    [resetNativeInputValue]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onPreviewChange(
        acceptedFiles.map(
          (file: File) =>
            Object.assign(file, {
              preview: file.type.startsWith('image/')
                ? URL.createObjectURL(file)
                : undefined,
            }) as FilePreview
        )
      );
    },
    [onPreviewChange]
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: settings.filePickerAcceptTypes,
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    validator: file => {
      // FIXME: Read more on WA2-7392
      const extension = file.name
        .split('.')
        .pop()
        ?.toLowerCase();
      if (extension && BLOCKED_EXTENSIONS.has(extension)) {
        return {
          code: 'file-blocked-extension',
          message: 'File extension is blocked',
        };
      }

      const isImage = file.type.startsWith('image/');
      const limitMB = isImage ? 5 : 16;
      if (file.size > limitMB * 1024 * 1024) {
        return {
          code: 'file-too-large',
          message: `${
            isImage ? 'Image' : 'File'
          } exceeds the ${limitMB}MB limit`,
        };
      }
      return null;
    },
    onDropRejected,
    onDrop,
    onDropAccepted: acceptedFile => {
      // This will be called when a file is successfully accepted
      if (onFileChange) {
        onFileChange(acceptedFile[0]);
      }
    },
  });

  function removeFilePreview(): void {
    onPreviewChange([]);
    if (onFileChange) {
      onFileChange(undefined);
    }

    resetNativeInputValue();
  }

  const thumbs = files.map((file: FilePreview) => (
    <Thumb key={file.name}>
      <RemoveImageButton
        onClick={() => removeFilePreview()}
        title={`Remove ${file.name}`}>
        ×
      </RemoveImageButton>
      <ThumbInner>
        {isImageFile(file) && file.preview ? (
          <ThumbImage
            src={file.preview}
            title={file.name}
            alt={file.name}
            onLoad={() => {
              URL.revokeObjectURL(file.preview as string);
            }}
          />
        ) : (
          <NonImagePreview title={file.name}>
            <NonImageBadge>{getFileExtension(file.name)}</NonImageBadge>
            <NonImageName>{file.name}</NonImageName>
          </NonImagePreview>
        )}
      </ThumbInner>
    </Thumb>
  ));

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => {
      files.forEach((file: FilePreview) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [files]);

  return (
    <section
      className="file-picker-preview-container"
      style={{ width: '100%' }}>
      <ThumbsContainer>{thumbs}</ThumbsContainer>
      <div
        {...getRootProps({
          className: 'dropzone',
        })}>
        <input
          {...getInputProps()}
          ref={fileInputRef}
          style={{ display: 'none' }}
          tabIndex={-1}
        />
        {children}
      </div>
    </section>
  );
}

FilePickerPreview.defaultProps = {
  children: null,
  fileInputRef: null,
};

export default FilePickerPreview;
